import { FC, useCallback, useMemo, useState } from 'react'
import { Badge, Button, Pagination, Space, Table, Tabs } from 'antd'
import { useSearchParams } from 'react-router-dom'
import qs from 'qs'
import PageLayout from '../../components/page-layout/page-layout'
import TableWrapper from '../../components/table-wrapper/table-wrapper'
import useEndpointQueryParams from '../../hooks/table-pages/use-endpoint-query-parameters'
import useTablePagination from '../../hooks/table-pages/use-table-pagination'
import { useGetTasksQuery } from '../../store/api/tasks-api'
import { ASSIGNMENT_APPROVAL_TASK_TYPE } from '../../helpers/entities/shipment/curator-approval'
import { getCuratorTasksColumns } from './constants/columns'
import { EndpointQueryParameters } from '../../types/strapi/strapi'
import HelpDrawer from './components/help-drawer'

type TabKey = 'new' | 'done'

function buildQueryParams(
    params: EndpointQueryParameters,
    tab: TabKey
): EndpointQueryParameters {
    const query: EndpointQueryParameters = {
        sort: ['createdAt:desc'],
        populate: {
            shipment: {
                populate: {
                    client: true,
                    user_audit: {
                        populate: {
                            created_by_permissions_user: {
                                fields: ['documentId', 'username'],
                            },
                        },
                    },
                    blankPreviewFiles: true,
                    shipment_positions: true,
                    calculated: true,
                    tasks: true,
                    stamp: true,
                },
            },
        },
        pagination: params.pagination,
    }

    query.filters = {
        type: { $eq: ASSIGNMENT_APPROVAL_TASK_TYPE },
        taskStatus:
            tab === 'new'
                ? { $in: ['Новая', 'В работе'] }
                : { $in: ['Завершена', 'Отменена'] },
    }
    return query
}

const CuratorTasksPage: FC = () => {
    const params = useEndpointQueryParams({ deserializeFilters: () => ({}) })
    const [searchParams, setSearchParams] = useSearchParams()
    const [tab, setTab] = useState<TabKey>('new')

    const queryParams = useMemo(() => buildQueryParams(params, tab), [params, tab])
    const { data, isFetching, refetch } = useGetTasksQuery(queryParams)

    const newCountParams = useMemo(() => buildQueryParams(params, 'new'), [params])
    const { data: newCountData } = useGetTasksQuery({
        ...newCountParams,
        pagination: { page: 1, pageSize: 1 },
    })

    const pagination = useTablePagination(params, data)
    const columns = useMemo(
        () => getCuratorTasksColumns({ showActions: tab === 'new' }),
        [tab]
    )

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    const newCount = newCountData?.meta.pagination.total ?? 0

    const onTabChange = (key: string) => {
        setTab(key as TabKey)
        const pageSize = params.pagination?.pageSize || 15
        const next = new URLSearchParams(searchParams)
        next.set('pagination', qs.stringify({ page: 1, pageSize }))
        setSearchParams(next, { replace: true })
    }

    return (
        <PageLayout
            title='Задачи куратора'
            beforeUser={
                <Space size={8}>
                    <HelpDrawer />
                    <Badge count={tab === 'done' ? newCount : 0} overflowCount={99}>
                        <Button onClick={() => refetch()} loading={isFetching}>
                            Обновить
                        </Button>
                    </Badge>
                </Space>
            }
        >
            <Tabs
                activeKey={tab}
                onChange={onTabChange}
                items={[
                    {
                        key: 'new',
                        label: `Новые${newCount ? ` (${newCount})` : ''}`,
                    },
                    {
                        key: 'done',
                        label: 'Завершенные',
                    },
                ]}
            />
            <TableWrapper style={{ flex: 'auto' }} filtersSummaryHeight={0}>
                <Table
                    bordered
                    columns={columns}
                    dataSource={data?.data ?? []}
                    loading={isFetching}
                    pagination={false}
                    rowKey='documentId'
                    size='small'
                    footer={tableFooter}
                />
            </TableWrapper>
        </PageLayout>
    )
}

export default CuratorTasksPage
